import { useId } from 'react';
import type { ReactNode } from 'react';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FORMULARABSCHNITT (Auftrag 17.09.2026, Abschnitt 15; Abschnitt 25)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Formular dieser Anwendung hat selten fünf Felder - die Anlagenseite
 * hat über vierzig. Ohne Gliederung ist das eine Wand. Dieser Abschnitt
 * nimmt eine fachliche Gruppe auf („Luftmengen", „Schallwerte",
 * „Modellbezug") und stellt die Felder in ein Gitter.
 *
 * `<fieldset>` mit `<legend>`: Das ist die einzige Auszeichnung, die einer
 * Vorlesehilfe die Gruppenzugehörigkeit eines Feldes mitteilt (Abschnitt
 * 28). Die sichtbare Gestalt der Legende wird per CSS gesetzt, nicht die
 * Auszeichnung getauscht.
 *
 * Die Feldbreite steuert das FELD (`breite: 'voll' | 'halb' | 'drittel'`),
 * nicht der Abschnitt - so kann eine Bemerkung über die ganze Breite laufen,
 * während daneben zwei Zahlen stehen.
 */

export interface FormSectionProps {
  titel?: string;
  beschreibung?: ReactNode;
  children: ReactNode;
  /** Knöpfe rechts neben der Überschrift. */
  aktionen?: ReactNode;
  /** Knöpfe am Fuß, z. B. [Speichern] [Verwerfen]. */
  fuss?: ReactNode;
  /** Spalten des Feldgitters. Vorgabe 2. */
  spalten?: 1 | 2 | 3;
  /** Der ganze Abschnitt ist gesperrt (fehlendes Recht, laufender Auftrag). */
  gesperrt?: boolean;
  /** Erklärt, WARUM gesperrt ist - ohne das wirkt es wie ein Fehler. */
  gesperrtGrund?: string;
  className?: string;
}

export function FormSection({
  titel, beschreibung, children, aktionen, fuss, spalten = 2,
  gesperrt, gesperrtGrund, className,
}: FormSectionProps) {
  const titelId = useId();

  return (
    <fieldset
      className={`m-formabschnitt${className ? ` ${className}` : ''}`}
      disabled={gesperrt}
      aria-describedby={gesperrt && gesperrtGrund ? `${titelId}-grund` : undefined}
    >
      {(titel || aktionen || beschreibung) && (
        <legend className="m-formabschnitt-legende" id={titelId}>
          <span className="m-formabschnitt-titel">{titel}</span>
        </legend>
      )}
      {(beschreibung || aktionen) && (
        <div className="m-formabschnitt-kopf">
          {beschreibung && (
            <p className="m-formabschnitt-beschreibung">{beschreibung}</p>
          )}
          {aktionen && <div className="m-formabschnitt-aktionen">{aktionen}</div>}
        </div>
      )}

      {gesperrt && gesperrtGrund && (
        <p className="m-formabschnitt-gesperrt" id={`${titelId}-grund`}>
          <span aria-hidden="true">i </span>{gesperrtGrund}
        </p>
      )}

      <div className={`m-feldgitter m-feldgitter-${spalten}`}>{children}</div>

      {fuss && <div className="m-formabschnitt-fuss">{fuss}</div>}
    </fieldset>
  );
}
